import React, { useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { useOrganization } from "../../context/OrganizationContext";
import {
  buildClientStatement,
  defaultStatementPeriod,
  formatStatementDate,
  loadClientStatementInvoices,
} from "../../helpers/clientStatement";
import { formatMoneyFromCents } from "../../helpers/invoiceMoney";
import { resolveOrgBranding } from "../../helpers/resolveOrgBranding";
import { downloadClientStatementPdf } from "../../pdf/statement/downloadStatementPdf";
import { formatSupabaseError } from "../../helpers/estimates";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import "./ClientStatementScreen.css";

const FIELD_STYLE = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid rgba(201,168,76,0.25)",
  background: "rgba(255,255,255,0.04)",
  color: "white",
  colorScheme: "dark",
  fontSize: "16px",
  boxSizing: "border-box",
};

const TILE_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "12px 14px",
};

function moneyOrDash(cents, blankIfZero = false) {
  if (blankIfZero && !cents) return "—";
  return formatMoneyFromCents(cents);
}

function SummaryTile({ label, cents, emphasis = false }) {
  return (
    <div style={TILE_STYLE}>
      <div style={{ fontSize: "12px", letterSpacing: "0.04em", opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: "20px", fontWeight: 800, marginTop: "4px", color: emphasis ? "#c9a84c" : "white" }}>
        {formatMoneyFromCents(cents)}
      </div>
    </div>
  );
}

export function ClientStatementScreen({
  currentUser,
  onBack,
  lockedClientId = "",
  lockedClientName = "",
  previewSource = null,
}) {
  const { settings } = useOrganization();
  const isDesktop = useIsDesktopViewport();
  const initialPeriod = useMemo(() => defaultStatementPeriod(new Date()), []);
  const [periodStart, setPeriodStart] = useState(initialPeriod.periodStart);
  const [periodEnd, setPeriodEnd] = useState(initialPeriod.periodEnd);
  const clientLocked = Boolean(lockedClientId);
  const [clientId, setClientId] = useState(lockedClientId || "");
  const [view, setView] = useState("activity");
  const [source, setSource] = useState(
    () => previewSource || { organizationId: "", invoices: [] },
  );
  const [loading, setLoading] = useState(() => !previewSource);
  const [error, setError] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (previewSource) return undefined;
    let cancelled = false;
    async function load() {
      if (shouldSkipSupabase(currentUser)) {
        setSource({ organizationId: "", invoices: [] });
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const loaded = await loadClientStatementInvoices(currentUser);
        if (!cancelled) setSource(loaded);
      } catch (loadError) {
        if (!cancelled) setError(formatSupabaseError(loadError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser, previewSource]);

  const clients = useMemo(() => {
    const byId = new Map();
    for (const invoice of source.invoices) {
      if (!invoice.client_id || byId.has(invoice.client_id)) continue;
      byId.set(invoice.client_id, invoice.clients?.client_name || "Client");
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [source.invoices]);

  useEffect(() => {
    if (clientLocked) {
      setClientId(lockedClientId);
      return;
    }
    if (!clientId && clients.length > 0) {
      setClientId(clients[0].id);
    }
  }, [clientId, clientLocked, clients, lockedClientId]);

  const statement = useMemo(() => {
    if (!clientId) return null;
    try {
      return buildClientStatement({
        clientId,
        organizationId: source.organizationId,
        invoices: source.invoices,
        periodStart,
        periodEnd,
      });
    } catch (buildError) {
      return { error: buildError.message };
    }
  }, [clientId, source, periodStart, periodEnd]);

  async function exportPdf() {
    if (!statement || statement.error) return;
    setExporting(true);
    setPdfError("");
    try {
      await downloadClientStatementPdf(statement, {
        branding: resolveOrgBranding(settings),
      });
    } catch (exportError) {
      setPdfError(exportError?.message || "The statement PDF could not be created.");
    } finally {
      setExporting(false);
    }
  }

  const statementError = statement?.error || "";

  return (
    <CrewPilotModuleShell
      title="Statement of Account"
      subtitle="Client activity from invoices and payments"
      sectionLabel="Finance"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
    >
      <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }}>
        {clientLocked ? (
          <div>
            <div style={{ marginBottom: "6px", fontSize: "13px", opacity: 0.75 }}>Client</div>
            <div style={{ fontWeight: 700 }}>
              {(statement?.clientName && statement.clientName !== "Client"
                ? statement.clientName
                : lockedClientName) || "Client"}
            </div>
          </div>
        ) : (
          <label>
            <div style={{ marginBottom: "6px", fontSize: "13px", opacity: 0.75 }}>Client</div>
            <select
              aria-label="Statement client"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              style={FIELD_STYLE}
              disabled={loading || clients.length === 0}
            >
              {clients.length === 0 ? <option value="">No issued invoices</option> : null}
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="statement-period">
          <label>
            <div style={{ marginBottom: "6px", fontSize: "13px", opacity: 0.75 }}>From</div>
            <input
              aria-label="Statement start date"
              type="date"
              value={periodStart}
              onChange={(event) => setPeriodStart(event.target.value)}
              style={FIELD_STYLE}
            />
          </label>
          <label>
            <div style={{ marginBottom: "6px", fontSize: "13px", opacity: 0.75 }}>To</div>
            <input
              aria-label="Statement end date"
              type="date"
              value={periodEnd}
              onChange={(event) => setPeriodEnd(event.target.value)}
              style={FIELD_STYLE}
            />
          </label>
        </div>
      </div>

      {loading ? <p style={{ opacity: 0.7 }}>Loading statement…</p> : null}
      {error ? <div style={{ color: "#e06b5a", marginBottom: "12px" }}>{error}</div> : null}
      {statementError ? <div style={{ color: "#e06b5a", marginBottom: "12px" }}>{statementError}</div> : null}

      {statement && !statement.error ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "14px" }}>
            <SummaryTile label="Opening balance" cents={statement.openingCents} />
            <SummaryTile label="Charges" cents={statement.chargesCents} />
            <SummaryTile label="Payments / credits" cents={statement.paymentsCents} />
            <SummaryTile label="Closing balance" cents={statement.closingCents} emphasis />
          </div>
          <p style={{ margin: "0 0 14px", opacity: 0.8, fontSize: "14px" }}>
            Statement date {formatStatementDate(statement.statementDate)}. Overdue as of that date:{" "}
            {formatMoneyFromCents(statement.overdueCents)}.
          </p>
          <div className="statement-actions">
            {[
              ["activity", "Activity"],
              ["open", "Open items"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setView(value)}
                style={{
                  borderRadius: "999px",
                  padding: "8px 14px",
                  fontWeight: 800,
                  cursor: "pointer",
                  border: view === value ? "1px solid rgba(201,168,76,0.45)" : "1px solid rgba(255,255,255,0.14)",
                  background: view === value ? "rgba(201,168,76,0.12)" : "rgba(255,255,255,0.06)",
                  color: view === value ? "#c9a84c" : "white",
                }}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              onClick={exportPdf}
              disabled={exporting}
              className="statement-pdf"
              style={{
                borderRadius: "999px",
                padding: "8px 14px",
                fontWeight: 800,
                cursor: "pointer",
                border: "1px solid rgba(201,168,76,0.45)",
                background: "#c9a84c",
                color: "#1a1a1a",
              }}
            >
              {exporting ? "Preparing PDF…" : "Download PDF"}
            </button>
          </div>
          {pdfError ? <div style={{ color: "#e06b5a", marginBottom: "12px" }}>{pdfError}</div> : null}

          {view === "activity" && !isDesktop ? (
            <div className="statement-cards">
              <article className="statement-card">
                <div>
                  <div className="statement-card__type">Opening balance</div>
                  <div>{formatStatementDate(statement.periodStart)}</div>
                </div>
                <strong>{moneyOrDash(statement.openingCents)}</strong>
              </article>
              {statement.activity.length === 0 ? (
                <p style={{ opacity: 0.75 }}>No activity in this period.</p>
              ) : (
                statement.activity.map((row) => {
                  const amount = row.chargeCents ? row.chargeCents : row.paymentCents ? -row.paymentCents : 0;
                  const signed = amount < 0
                    ? `-${formatMoneyFromCents(Math.abs(amount))}`
                    : `+${formatMoneyFromCents(amount)}`;
                  return (
                    <article key={row.id} className="statement-card">
                      <div>
                        <div className="statement-card__type">{row.transaction}</div>
                        <div>{formatStatementDate(row.date)}</div>
                        <div className="statement-card__ref">{row.reference}</div>
                        {row.projectName ? <div className="statement-card__ref">{row.projectName}</div> : null}
                      </div>
                      <div className="statement-card__amounts">
                        <strong className={amount < 0 ? "is-payment" : "is-charge"}>{signed}</strong>
                        <span>Balance {moneyOrDash(row.balanceCents)}</span>
                      </div>
                    </article>
                  );
                })
              )}
              <article className="statement-card">
                <div>
                  <div className="statement-card__type">Closing balance</div>
                  <div>{formatStatementDate(statement.periodEnd)}</div>
                </div>
                <strong>{moneyOrDash(statement.closingCents)}</strong>
              </article>
            </div>
          ) : view === "activity" ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "640px", fontSize: "14px" }}>
                <thead>
                  <tr>
                    {["Date", "Transaction", "Reference", "Charges", "Payments / credits", "Balance"].map((heading) => (
                      <th key={heading} style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: "8px" }}>{formatStatementDate(statement.periodStart)}</td>
                    <td style={{ padding: "8px" }}>Opening balance</td>
                    <td style={{ padding: "8px" }}>—</td>
                    <td style={{ padding: "8px" }}>—</td>
                    <td style={{ padding: "8px" }}>—</td>
                    <td style={{ padding: "8px" }}>{moneyOrDash(statement.openingCents)}</td>
                  </tr>
                  {statement.activity.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: "8px", opacity: 0.7 }}>
                        No activity in this period.
                      </td>
                    </tr>
                  ) : (
                    statement.activity.map((row) => (
                      <tr key={row.id}>
                        <td style={{ padding: "8px" }}>{formatStatementDate(row.date)}</td>
                        <td style={{ padding: "8px" }}>
                          {row.transaction}
                          {row.projectName ? (
                            <div style={{ opacity: 0.65, fontSize: "12px" }}>{row.projectName}</div>
                          ) : null}
                        </td>
                        <td style={{ padding: "8px" }}>{row.reference}</td>
                        <td style={{ padding: "8px" }}>{moneyOrDash(row.chargeCents, true)}</td>
                        <td style={{ padding: "8px" }}>{moneyOrDash(row.paymentCents, true)}</td>
                        <td style={{ padding: "8px" }}>{moneyOrDash(row.balanceCents)}</td>
                      </tr>
                    ))
                  )}
                  <tr>
                    <td style={{ padding: "8px", fontWeight: 800 }}>{formatStatementDate(statement.periodEnd)}</td>
                    <td style={{ padding: "8px", fontWeight: 800 }}>Closing balance</td>
                    <td style={{ padding: "8px" }}>—</td>
                    <td style={{ padding: "8px" }}>{moneyOrDash(statement.chargesCents)}</td>
                    <td style={{ padding: "8px" }}>{moneyOrDash(statement.paymentsCents)}</td>
                    <td style={{ padding: "8px", fontWeight: 800 }}>{moneyOrDash(statement.closingCents)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : !isDesktop ? (
            <div className="statement-cards">
              {statement.openItems.length === 0 ? (
                <p style={{ opacity: 0.75 }}>No invoices have a balance on the statement date.</p>
              ) : (
                statement.openItems.map((item) => (
                  <article key={item.invoiceId} className="statement-card">
                    <div>
                      <div className="statement-card__type">{item.invoiceNumber}</div>
                      <div className="statement-card__ref">{item.projectName || "No project"}</div>
                      <div>
                        Due {formatStatementDate(item.dueDate)}
                        {item.overdue ? <span style={{ color: "#e06b5a" }}> · Overdue</span> : null}
                      </div>
                    </div>
                    <strong>{moneyOrDash(item.balanceCents)}</strong>
                  </article>
                ))
              )}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              {statement.openItems.length === 0 ? (
                <p style={{ opacity: 0.75 }}>No invoices have a balance on the statement date.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px", fontSize: "14px" }}>
                  <thead>
                    <tr>
                      {["Invoice", "Project", "Due", "Balance"].map((heading) => (
                        <th key={heading} style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {statement.openItems.map((item) => (
                      <tr key={item.invoiceId}>
                        <td style={{ padding: "8px" }}>{item.invoiceNumber}</td>
                        <td style={{ padding: "8px" }}>{item.projectName || "—"}</td>
                        <td style={{ padding: "8px" }}>
                          {formatStatementDate(item.dueDate)}
                          {item.overdue ? <div style={{ color: "#e06b5a" }}>Overdue</div> : null}
                        </td>
                        <td style={{ padding: "8px" }}>{moneyOrDash(item.balanceCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {statement.limitations.length > 0 ? (
            <ul style={{ marginTop: "16px", color: "#e0c27a" }}>
              {statement.limitations.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          ) : null}
          <p style={{ marginTop: "16px", fontSize: "13px", opacity: 0.65 }}>{statement.calculationNote}</p>
        </>
      ) : null}
    </CrewPilotModuleShell>
  );
}
